import { getAssistance } from "../../../lib/admin-assistance";
import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router";
import { useConfirm } from "../../../components/ConfirmDialog";
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from "../../../components/ui/Dialog";
import { getVenueStatus } from "../../provider-availability/availability";
import { AvailabilityCard } from "../../provider-availability/AvailabilityCard";
import { GamesCard } from "../GamesCard";
import { BlackoutWindowsCard } from "../BlackoutWindowsCard";
import { ProviderPhotoManager } from "../ProviderPhotoManager";
import { VenueLogoManager } from "../VenueLogoManager";
import { UnitFormCard, emptyUnitForm } from "../UnitFormCard";
import { VenueFormCard, emptyVenueForm } from "../VenueFormCard";
import { VenueList } from "../VenueList";
import {
  countText,
  formatLabel,
  getVenueApprovalTone,
  resolveProviderMembershipRole,
  venueCanOperate,
  venueCanConfigure,
} from "../provider-workspace-helpers";
import { useProviderWorkspace } from "../ProviderWorkspaceContext";
import { useProviderWorkspaceForm } from "../ProviderWorkspaceFormContext";
import type { ProviderUnit, UnitType } from "../../../lib/api";

type SetupTab = "info" | "units" | "games" | "food-drink" | "hours";

function getUnitTypeBadge(type: UnitType) {
  switch (type) {
    case "console":
      return "CN";
    case "pc":
      return "PC";
    case "vr":
      return "VR";
    case "simulator":
      return "SI";
    case "billiards":
      return "BL";
    case "pingpong":
      return "PP";
    case "room":
      return "RM";
    case "other":
    default:
      return "OT";
  }
}

function groupUnitsByType(units: ProviderUnit[]) {
  const grouped = new Map<UnitType, ProviderUnit[]>();
  for (const unit of units) {
    const list = grouped.get(unit.type) ?? [];
    list.push(unit);
    grouped.set(unit.type, list);
  }
  return grouped;
}

export function VenueSetupSection() {
  const [searchParams] = useSearchParams();
  const tabParam = searchParams.get("tab");
  const {
    venues,
    selectedVenue,
    selectedUnit,
    selectedVenueId,
    selectedUnitId,
    setSelectedUnitId,
    isLoading,
    creatingVenue,
    updatingVenue,
    deletingVenue,
    creatingUnit,
    updatingUnit,
    deletingUnit,
    updatingAvailability,
    creatingBlackoutWindow,
    deletingBlackoutWindowId,
    createVenue,
    updateVenue,
    deleteVenue,
    createUnit,
    updateUnit,
    deleteUnit,
    updateAvailability,
    createBlackoutWindow,
    deleteBlackoutWindow,
    handleSelectVenue,
    handleDeleteVenue,
    handleDeleteUnit,
    syncOperationalState,
    navigateToSection,
    summary,
    user,
    t,
  } = useProviderWorkspace();
  const {
    venuePanel,
    setVenuePanel,
    venueRemovalNote,
    setVenueRemovalNote,
    venueRemovalError,
    setVenueRemovalError,
  } = useProviderWorkspaceForm();

  const [activeTab, setActiveTab] = useState<SetupTab>(
    tabParam === "units" || tabParam === "games" || tabParam === "food-drink" || tabParam === "hours"
      ? tabParam
      : "info",
  );
  const [isFormDirty, setIsFormDirty] = useState(false);
  const { confirm, dialog } = useConfirm();

  useEffect(() => {
    const nextTab = tabParam === "units" || tabParam === "games" || tabParam === "food-drink" || tabParam === "hours" ? tabParam : "info";
    setActiveTab(nextTab);
  }, [tabParam]);

  useEffect(() => {
    if (!venuePanel) {
      setIsFormDirty(false);
    }
  }, [venuePanel]);

  const requestTabChange = async (nextTab: SetupTab) => {
    if (venuePanel && isFormDirty) {
      const shouldDiscard = await confirm({
        confirmButtonLabel: t("Discard changes"),
        description: t("You have unsaved changes. Switching tabs will discard them."),
        title: t("Discard unsaved changes?"),
      });
      if (!shouldDiscard) {
        return;
      }
    }
    setVenuePanel(null);
    setActiveTab(nextTab);
  };

  const selectedVenueOpenState = selectedVenue
    ? getVenueStatus(selectedVenue, t)
    : null;
  const canManageVenueSetup = resolveProviderMembershipRole(summary, user) === "owner";
  const venueSetupOwnerHint = canManageVenueSetup
    ? undefined
    : t("Only owners can change venue setup.");
  const setupChecklist: Array<[string, boolean]> = selectedVenue ? [
    ["Map pin", selectedVenue.latitude !== null && selectedVenue.longitude !== null],
    ["Venue photos", (selectedVenue.photos?.length ?? 0) > 0 || !!selectedVenue.image_url],
    ["Logo", !!selectedVenue.logo_url],
    ["Priced units", selectedVenue.units.length > 0 && selectedVenue.units.every(unit => unit.base_hourly_rate > 0)],
    ["Opening hours", selectedVenue.operating_hours.some(hour => hour.is_open)],
  ] : [];
  const selectedVenueCanConfigure = selectedVenue ? venueCanConfigure(selectedVenue.approval_status) : false;
  const selectedVenueCanOperate = selectedVenue ? venueCanOperate(selectedVenue.approval_status) : false;

  const selectedVenueForm = useMemo(
    () =>
      selectedVenue
        ? {
            name: selectedVenue.name,
            city: selectedVenue.city,
            timezone: selectedVenue.timezone,
            address: selectedVenue.address,
            image_url: selectedVenue.image_url ?? "",
            logo_url: selectedVenue.logo_url ?? "",
            latitude: selectedVenue.latitude ?? null,
            longitude: selectedVenue.longitude ?? null,
          }
        : emptyVenueForm,
    [selectedVenue],
  );

  const selectedUnitForm = useMemo(
    () =>
      selectedUnit
        ? {
            name: selectedUnit.name,
            type: selectedUnit.type,
            status:
              selectedUnit.base_status === "occupied"
                ? "available"
                : selectedUnit.base_status,
            base_hourly_rate: selectedUnit.base_hourly_rate ?? 0,
            capacity: selectedUnit.capacity,
            is_vip: selectedUnit.is_vip,
            supported_activities: selectedUnit.supported_activities,
            amenities: selectedUnit.amenities,
            expected_capability_revision: selectedUnit.capability_revision,
            peak_pricing_windows: (selectedUnit.peak_pricing_windows ?? []).map((window) => ({
              day_of_week: window.day_of_week,
              start_time: window.start_time,
              end_time: window.end_time,
              hourly_rate: window.hourly_rate,
            })),
          }
        : emptyUnitForm,
    [selectedUnit],
  );

  const groupedUnits = selectedVenue ? groupUnitsByType(selectedVenue.units) : new Map<UnitType, ProviderUnit[]>();

  // Venue/unit form panels open as focused dialogs; hours/blackouts stay inline.
  const formPanelOpen =
    venuePanel === "add-venue" ||
    venuePanel === "edit-venue" ||
    venuePanel === "add-unit" ||
    venuePanel === "edit-unit";
  const formPanelIsUnit = venuePanel === "add-unit" || venuePanel === "edit-unit";
  const formPanelTitle =
    venuePanel === "add-venue"
      ? t("New venue request")
      : venuePanel === "edit-venue" && selectedVenue
        ? t("Edit {name}", { name: selectedVenue.name })
        : venuePanel === "add-unit" && selectedVenue
          ? t("Add unit to {name}", { name: selectedVenue.name })
          : venuePanel === "edit-unit" && selectedUnit
            ? t("Edit {name}", { name: selectedUnit.name })
            : t("Edit venue");

  // Guards against re-entrant opens: while the discard confirm is up, Radix
  // outside-dismiss events (backdrop clicks, Escape) keep firing
  // requestFormPanelClose, and useConfirm would silently replace the pending
  // request instead of answering the one the user is looking at.
  const discardConfirmPendingRef = useRef(false);

  const requestFormPanelClose = () => {
    if (!isFormDirty) {
      setVenuePanel(null);
      return;
    }
    if (discardConfirmPendingRef.current) {
      return;
    }
    discardConfirmPendingRef.current = true;
    // Defer one task: when this runs from Radix's Escape handler, React
    // mounts ConfirmDialog before the same keydown finishes bubbling, so
    // its window listener would instantly cancel the freshly opened dialog.
    window.setTimeout(() => {
      if (!discardConfirmPendingRef.current) {
        return;
      }
      void confirm({
        confirmButtonLabel: t("Discard changes"),
        description: t("You have unsaved changes. Closing will discard them."),
        title: t("Discard unsaved changes?"),
      }).then((shouldDiscard) => {
        discardConfirmPendingRef.current = false;
        if (shouldDiscard) {
          setVenuePanel(null);
        }
      });
    }, 0);
  };

  const panelNode =
    venuePanel === "add-venue" ? (
      <VenueFormCard
        description=""
        initialValue={emptyVenueForm}
        isSubmitting={creatingVenue}
        onDirtyChange={setIsFormDirty}
        onSubmit={async (payload) => { await createVenue(payload); setVenuePanel(null); }}
        requiresApprovalNote
        submitLabel={t("Submit for approval")}
        title={t("New venue request")}
      />
    ) : venuePanel === "edit-venue" && selectedVenue && selectedVenue.approval_status !== "removed" ? (
      <VenueFormCard
        actions={
          <div className="grid gap-3">
            <div className="ops-subtle-surface grid gap-2 p-3">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <p className="ops-label">{t("Removal approval")}</p>
                <span className="ops-chip" data-tone={getVenueApprovalTone(selectedVenue.approval_status)}>
                  {formatLabel(selectedVenue.approval_status, t)}
                </span>
              </div>
              {selectedVenue.removal_note ? (
                <p className="text-xs leading-5 text-slate-400">{t("Current request: {note}", { note: selectedVenue.removal_note })}</p>
              ) : null}
              {venueRemovalError ? (
                <div className="rounded-[14px] border border-[var(--color-error)]/30 bg-[var(--color-error-m)] px-3 py-2 text-xs text-[var(--color-error)]" role="alert">
                  {venueRemovalError}
                </div>
              ) : null}
              <textarea
                className="ops-input min-h-[64px]"
                disabled={selectedVenue.approval_status !== "approved" || deletingVenue}
                onChange={(event) => {
                  setVenueRemovalNote(event.target.value);
                  if (venueRemovalError) setVenueRemovalError(null);
                }}
                placeholder={t("Explain why this physical venue should be removed from gaamed.")}
                value={venueRemovalNote}
              />
              <button
                className="ops-button w-full"
                data-size="sm"
                data-variant="danger"
                disabled={selectedVenue.approval_status !== "approved" || deletingVenue || venueRemovalNote.trim().length < 10}
                onClick={() => void handleDeleteVenue()}
                type="button"
              >
                {deletingVenue ? t("Sending request...") : t("Request removal approval")}
              </button>
            </div>
          </div>
        }
        description=""
        initialValue={selectedVenueForm}
        isSubmitting={updatingVenue}
        onDirtyChange={setIsFormDirty}
        onSubmit={async (payload) => { await updateVenue(selectedVenue.id, payload); setVenuePanel(null); }}
        submitLabel={t("Save venue")}
        title={t("Edit {name}", { name: selectedVenue.name })}
      />
    ) : venuePanel === "hours" && selectedVenue && selectedVenueCanConfigure ? (
      <AvailabilityCard
        disabled={!canManageVenueSetup}
        disabledHint={venueSetupOwnerHint}
        isSubmitting={updatingAvailability}
        onSubmit={async (operatingHours) => { await updateAvailability(selectedVenue.id, operatingHours); setVenuePanel(null); }}
        timezone={selectedVenue.timezone}
        value={selectedVenue.operating_hours}
      />
    ) : venuePanel === "blackouts" && selectedVenue && selectedVenueCanConfigure ? (
      <BlackoutWindowsCard
        deletingBlackoutWindowId={deletingBlackoutWindowId}
        isSubmitting={creatingBlackoutWindow}
        onCreate={async (payload) => { await createBlackoutWindow(selectedVenue.id, payload); }}
        onDelete={async (blackoutId) => { await deleteBlackoutWindow(selectedVenue.id, blackoutId); }}
        timezone={selectedVenue.timezone}
        value={selectedVenue.blackout_windows ?? []}
      />
    ) : venuePanel === "add-unit" && selectedVenue && selectedVenueCanConfigure ? (
      <UnitFormCard
        description=""
        disabled={false}
        initialValue={emptyUnitForm}
        isSubmitting={creatingUnit}
        onDirtyChange={setIsFormDirty}
        onSubmit={async (payload) => { await createUnit(selectedVenue.id, payload); setVenuePanel(null); }}
        selectedVenue={selectedVenue}
        submitLabel={t("Create unit")}
        title={t("Add unit to {name}", { name: selectedVenue.name })}
      />
    ) : venuePanel === "edit-unit" && selectedVenue && selectedUnit && selectedVenueCanConfigure ? (
      <UnitFormCard
        actions={
          <div className="grid gap-3">
            <ProviderPhotoManager
              limit={6}
              onUpdated={async () => {
                await syncOperationalState(selectedVenue.id, selectedUnit.id);
              }}
              ownerId={selectedUnit.id}
              ownerType="unit"
              photos={selectedUnit.photos ?? []}
              title={selectedUnit.name}
              venueId={selectedVenue.id}
            />
            <button className="ops-button w-full" data-size="sm" data-variant="danger" disabled={deletingUnit} onClick={() => void handleDeleteUnit(selectedUnit.id, selectedUnit.name)} type="button">
              {deletingUnit ? t("Deleting...") : t("Delete unit")}
            </button>
          </div>
        }
        description=""
        initialValue={selectedUnitForm}
        isSubmitting={updatingUnit}
        onDirtyChange={setIsFormDirty}
        onSubmit={async (payload) => { await updateUnit(selectedVenue.id, selectedUnit.id, payload); setVenuePanel(null); }}
        selectedVenue={selectedVenue}
        submitLabel={t("Save unit")}
        title={t("Edit {name}", { name: selectedUnit.name })}
      />
    ) : null;

  return (
    <div className="grid gap-4">
      {dialog}
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="ops-kicker">{t("Setup")}</p>
          <h1 className="ops-page-title">{t("Venue setup")}</h1>
          <p className="ops-copy mt-1 max-w-2xl text-sm">{t("What players see in the app: photos, units and prices, opening hours and games.")}</p>
        </div>
        <span title={venueSetupOwnerHint}>
            <button
              className="ops-button"
              data-size="sm"
              data-variant="primary"
              disabled={!canManageVenueSetup}
              onClick={() => setVenuePanel(venuePanel === "add-venue" ? null : "add-venue")}
              type="button"
            >
              {t("Add a venue")}
            </button>
          </span>
      </header>
      {/* The sidebar already switches venues; the list helps only when there are several, or none yet. */}
      {venues.length !== 1 ? (
        <div className="ops-surface">
          <p className="ops-label mb-3">{t("Your venues")}</p>
          <VenueList
            isLoading={isLoading}
            onSelect={handleSelectVenue}
            selectedVenueId={selectedVenueId}
            venues={venues}
          />
        </div>
      ) : null}

      <div className="grid gap-4">
        {selectedVenue ? (
          <div className="ops-surface">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="min-w-0">
                <h2 className="line-clamp-2 text-base font-semibold text-white">{selectedVenue.name}</h2>
                <p className="truncate text-xs text-slate-400">
                  {selectedVenue.address} · {selectedVenue.city}
                </p>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <span className="ops-chip" data-tone={selectedVenueOpenState?.isOpenNow ? "success" : "warning"}>
                  {selectedVenueOpenState?.reason}
                </span>
                <span className="ops-chip" data-tone={getVenueApprovalTone(selectedVenue.approval_status)}>
                  {formatLabel(selectedVenue.approval_status, t)}
                </span>
              </div>
              <div className="flex flex-wrap items-center gap-2" role="tablist">
                <button aria-controls="venue-tab-panel" aria-selected={activeTab === "info"} className="ops-button" data-size="sm" data-variant={activeTab === "info" ? "primary" : "secondary"} id="venue-tab-info" onClick={() => void requestTabChange("info")} role="tab" type="button">{t("Info")}</button>
                <button aria-controls="venue-tab-panel" aria-selected={activeTab === "units"} className="ops-button" data-size="sm" data-variant={activeTab === "units" ? "primary" : "secondary"} id="venue-tab-units" onClick={() => void requestTabChange("units")} role="tab" type="button">{t("Units")}</button>
                <button aria-controls="venue-tab-panel" aria-selected={activeTab === "hours"} className="ops-button" data-size="sm" data-variant={activeTab === "hours" ? "primary" : "secondary"} id="venue-tab-hours" onClick={() => void requestTabChange("hours")} role="tab" type="button">{t("Hours")}</button>
                <button aria-controls="venue-tab-panel" aria-selected={activeTab === "games"} className="ops-button" data-size="sm" data-variant={activeTab === "games" ? "primary" : "secondary"} id="venue-tab-games" onClick={() => void requestTabChange("games")} role="tab" type="button">{t("Games")}</button>
                <button className="ops-button" data-size="sm" data-variant="secondary" onClick={() => navigateToSection("menu")} type="button">{t("Menu & stock")} ↗</button>
              </div>
            </div>

            {!selectedVenueCanOperate ? (
              <div className="ops-banner mt-3" data-tone="warning" role="status">
                {t("Prepare the photos, units, prices, games, menu and hours now. This venue stays hidden from customers until admin approval publishes it.")}
              </div>
            ) : null}

            {canManageVenueSetup && (getAssistance() || !selectedVenueCanOperate || !setupChecklist.every(([, complete]) => complete)) && <div className="mt-3 rounded-xl border border-white/10 p-4">
              <p className="ops-label">{t(getAssistance() ? "Before inviting the owner" : "Venue setup checklist")}</p>
              <ul className="mt-2 flex flex-wrap gap-x-5 gap-y-2 text-sm">
                {setupChecklist.map(([label, complete]) => <li key={String(label)} className="ops-chip" data-tone={complete ? "success" : "warning"}>{complete ? "✓" : "○"} {t(String(label))}</li>)}
              </ul>
              <p className="mt-2 text-xs text-slate-400">{t(getAssistance() ? "Confirm the actual schedule, available games and menu. Exit assistance, approve the venue to publish it, then send the owner a setup link." : selectedVenueCanOperate ? "Finish these so players see a complete venue." : "Confirm your opening hours, available games and menu before the venue is approved.")}</p>
            </div>}

            <div aria-labelledby={`venue-tab-${activeTab}`} id="venue-tab-panel" role="tabpanel">
              {activeTab === "info" ? (
                <div className="mt-3 grid gap-3">
                  <section className="rounded-xl border border-white/10 p-4">
                    <div className="flex flex-wrap items-start justify-between gap-2">
                      <p className="ops-label">{t("Details")}</p>
                      <span title={venueSetupOwnerHint}>
                        <button className="ops-button" data-size="sm" data-variant="secondary" disabled={!canManageVenueSetup} onClick={() => setVenuePanel(venuePanel === "edit-venue" ? null : "edit-venue")} type="button">{t("Edit details")}</button>
                      </span>
                    </div>
                    <dl className="venue-facts mt-2">
                      <div><dt>{t("Name")}</dt><dd>{selectedVenue.name}</dd></div>
                      <div><dt>{t("Address")}</dt><dd>{selectedVenue.address.toLowerCase().includes(selectedVenue.city.toLowerCase()) ? selectedVenue.address : `${selectedVenue.address}, ${selectedVenue.city}`}</dd></div>
                      <div><dt>{t("Map pin")}</dt><dd>{selectedVenue.latitude !== null && selectedVenue.longitude !== null
                        ? <a className="underline" href={`https://www.google.com/maps?q=${selectedVenue.latitude},${selectedVenue.longitude}`} rel="noreferrer" target="_blank">{t("Open in Maps")}</a>
                        : <span className="ops-chip" data-tone="warning">{t("Not set. Players can't get directions.")}</span>}</dd></div>
                      <div><dt>{t("Time zone")}</dt><dd><bdi>{selectedVenue.timezone}</bdi></dd></div>
                    </dl>
                  </section>
                  <ProviderPhotoManager
                    limit={8}
                    onUpdated={async () => {
                      await syncOperationalState(selectedVenue.id, selectedUnitId);
                    }}
                    ownerId={selectedVenue.id}
                    ownerType="venue"
                    photos={selectedVenue.photos ?? []}
                    title={selectedVenue.name}
                  />
                  <VenueLogoManager
                    canManage={canManageVenueSetup}
                    onSave={async (logoUrl) => {
                      await updateVenue(selectedVenue.id, {
                        address: selectedVenue.address,
                        city: selectedVenue.city,
                        image_url: selectedVenue.image_url,
                        logo_url: logoUrl,
                        name: selectedVenue.name,
                        timezone: selectedVenue.timezone,
                      });
                    }}
                    venue={selectedVenue}
                  />
                </div>
              ) : activeTab === "units" ? (
                <div className="mt-3">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <p className="text-xs text-slate-400">{countText(t, selectedVenue.units.length, "{count} unit", "{count} units")}</p>
                    <span title={venueSetupOwnerHint}>
                      <button className="ops-button" data-size="sm" data-variant="primary" disabled={!selectedVenueCanConfigure || !canManageVenueSetup} onClick={() => setVenuePanel(venuePanel === "add-unit" ? null : "add-unit")} type="button">
                        {t("+ Unit")}
                      </button>
                    </span>
                  </div>
                  {!selectedVenueCanConfigure ? (
                    <div className="ops-banner mt-3" data-tone="warning" role="status">
                      {t("This venue cannot be configured while removal is pending or complete.")}
                    </div>
                  ) : null}
                  {selectedVenue.units.length > 0 ? (
                    <div className="mt-3 grid gap-4">
                      {Array.from(groupedUnits.entries()).map(([type, units]) => (
                        <div className="grid gap-2" key={type}>
                          <p className="ops-label px-1">{formatLabel(type, t)}</p>
                          {units.map((unit) => (
                            <div
                              className={`ops-unit-row !flex min-w-0 flex-wrap items-center gap-2 ${unit.id === selectedUnitId ? "border-[var(--color-signal)]/40 bg-[var(--color-signal-muted)]" : ""}`}
                              data-status={unit.status}
                              key={unit.id}
                            >
                              <div className="flex min-w-0 flex-1 items-center gap-2">
                                <span className="ops-status-dot shrink-0" />
                                <div className="grid h-8 w-8 shrink-0 place-items-center rounded-[8px] border border-white/10 bg-white/10 text-[0.6rem] font-black text-white">
                                  {getUnitTypeBadge(unit.type)}
                                </div>
                                <span className="min-w-0" title={venueSetupOwnerHint}>
                                  <button
                                    className="min-w-0 text-start"
                                    disabled={!canManageVenueSetup}
                                    onClick={() => { setSelectedUnitId(unit.id); setVenuePanel("edit-unit"); }}
                                    type="button"
                                  >
                                    <p className="truncate text-sm font-semibold text-white">{unit.name}</p>
                                    <p className="truncate text-xs text-slate-400">
                                      {formatLabel(unit.type, t)} · {t("Cap {count}", { count: unit.capacity })}
                                    </p>
                                  </button>
                                </span>
                              </div>
                              <div className="flex min-w-0 flex-wrap items-center gap-1.5">
                                {unit.is_vip ? <span className="ops-chip" data-tone="warning">{t("VIP")}</span> : null}
                                {unit.current_session ? <span className="ops-chip" data-tone="info">{t("Live")}</span> : null}
                                <span className="ops-chip">{formatLabel(unit.status, t)}</span>
                              </div>
                              <div className="flex shrink-0 items-center gap-1.5 sm:ml-auto">
                                <span title={venueSetupOwnerHint}>
                                  <button
                                    className="ops-button"
                                    data-size="sm"
                                    data-variant="secondary"
                                    disabled={!canManageVenueSetup}
                                    onClick={() => { setSelectedUnitId(unit.id); setVenuePanel("edit-unit"); }}
                                    type="button"
                                  >
                                    {t("Edit")}
                                  </button>
                                </span>
                                <span title={venueSetupOwnerHint}>
                                  <button
                                    className="ops-button"
                                    data-size="sm"
                                    data-variant="danger"
                                    disabled={!canManageVenueSetup}
                                    onClick={() => void handleDeleteUnit(unit.id, unit.name)}
                                    type="button"
                                  >
                                    {t("Delete")}
                                  </button>
                                </span>
                              </div>
                            </div>
                          ))}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="mt-3 text-sm text-slate-400">{t("No units yet — add one above.")}</p>
                  )}
                </div>
              ) : activeTab === "games" ? (
                <div className="mt-3 grid gap-4">
                  {selectedVenueCanConfigure ? (
                    <GamesCard units={selectedVenue.units} venueId={selectedVenue.id} venueName={selectedVenue.name} />
                  ) : (
                    <div className="ops-banner" data-tone="warning">{t("This venue cannot be configured while removal is pending or complete.")}</div>
                  )}
                </div>
              ) : activeTab === "food-drink" ? (
                <div className="mt-3 grid gap-4">
                  <div className="ops-banner" data-tone="info">
                    <span className="min-w-0 flex-1">{t("The food and drink menu now lives in Menu & stock, together with stock counts.")}</span>
                    <button className="ops-button" data-size="sm" data-variant="primary" onClick={() => navigateToSection("menu")} type="button">{t("Open Menu & stock")}</button>
                  </div>
                </div>
              ) : activeTab === "hours" ? (
                <div className="mt-3">
                  <AvailabilityCard
                    disabled={!canManageVenueSetup}
                    disabledHint={venueSetupOwnerHint}
                    isSubmitting={updatingAvailability}
                    onSubmit={async (operatingHours) => { await updateAvailability(selectedVenue.id, operatingHours); }}
                    timezone={selectedVenue.timezone}
                    value={selectedVenue.operating_hours}
                  />
                </div>
              ) : null}
            </div>
          </div>
        ) : null}

        {formPanelOpen ? null : panelNode}
      </div>

      <Dialog
        onOpenChange={(open) => {
          if (!open) {
            requestFormPanelClose();
          }
        }}
        open={formPanelOpen && panelNode !== null}
      >
        <DialogContent
          aria-describedby={undefined}
          className={`max-h-[90vh] overflow-y-auto border-0 bg-transparent p-0 shadow-none ${formPanelIsUnit ? "max-w-3xl" : "max-w-2xl"}`}
        >
          <DialogTitle className="sr-only">{formPanelTitle}</DialogTitle>
          {formPanelOpen ? panelNode : null}
        </DialogContent>
      </Dialog>
    </div>
  );
}
